/**
 * 配置版本（`#config-versions`）——新建页（判定卡 09、线框 03）。PR0 先出页头 ＋ 空态；PR6 出只读内容：
 * ① 快照列表（A-33：快照号 · 保存时间 · 谁 · 改了哪几项 · 备注；当前生效 ／ 种子标记；新 → 旧；完整翻页）
 * ② 前后对比抽屉（A-35 本快照 vs 上一个；种子快照没有上一版 → A-34 显示完整配置）
 * 从任意页点快照号进来（`?snapshot=…#config-versions`）→ 本页列表 ＋ 直接打开该快照的抽屉（O-8）；找不到 → 一行提示，列表照常。
 * MVP-0 只读：没有编辑、没有「恢复到此快照」（P1），页头无实色按钮。
 * 收尾 B1（配置版本第二步）：列表上方「运行环境」卡——生效快照里影响运行的环境项（画像版本、工具后端、Jev 锁定版本、
 * 推荐 ／ 不推荐分档线）与运行时实际取到的值、业务包与业务参数指纹（随代码走，记进每批 ／ 每轮运行资产）。
 * 标签体系 v2 批 5：每个快照记下保存时的标签字典版本（列表一列、抽屉说明里一段）；运行环境写当前字典版本和生效快照记的版本。
 */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { CodeBlock } from "@/components/ui/code-block";
import { Drawer } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { Pagination } from "@/components/ui/pagination";
import {
	PLAN_SEED_OPERATOR,
	type PlanLlmProvider,
	type PlanSnapshotRow,
	planErrorMessage,
	usePlanConfigDiff,
	usePlanRunEnv,
	usePlanSnapshot,
	usePlanSnapshots,
} from "@/lib/api/plan-lab";
import { cn } from "@/lib/utils";

import { FailState } from "../kit/fail-state";
import { PageJump } from "../kit/page-jump";
import { CellTags, PlanTable } from "../kit/plan-table";
import { SnapshotTag } from "../kit/snapshot-tag";
import { TextDiff } from "../kit/text-diff";

const PAGE = 20;

function fmtAt(at: string | null | undefined) {
	return at ? at.slice(5, 16).replace("T", " ") : "—";
}
const isSeed = (r: PlanSnapshotRow) => r.operator === PLAN_SEED_OPERATOR;
const opName = (op: string | null) => (op === PLAN_SEED_OPERATOR ? "初始化脚本" : (op ?? "—"));

/** 配置项属于哪一页（线框 03：改动项前面写所属页） */
function pageOf(key: string): string {
	if (
		key.startsWith("plan.prompt.") ||
		key.startsWith("plan.node.") ||
		key.startsWith("plan.rank.")
	)
		return "模型提示";
	if (key.startsWith("plan.budget.")) return "运行规则";
	if (key === "plan.recall.profile_version") return "候选召回";
	if (key.startsWith("plan.check.")) return "事项看板";
	return "运行环境";
}

const PAGE_ORDER = ["模型提示", "事项看板", "运行规则", "运行环境"];
const pageRank = (key: string) => PAGE_ORDER.indexOf(pageOf(key));

function show(v: unknown): string {
	if (v === undefined || v === null) return "（空）";
	if (typeof v === "string") return v;
	if (typeof v === "boolean") return v ? "开" : "关";
	return JSON.stringify(v, null, 2);
}

type Item = { key: string; label: string; before?: unknown; after: unknown; long: boolean };

function ItemList({
	items,
	sel,
	onSel,
}: {
	items: Item[];
	sel: string | null;
	onSel: (k: string) => void;
}) {
	return (
		<div className="flex flex-col gap-0.5" data-testid="plan-config-items">
			{items.map((it) => (
				<button
					key={it.key}
					type="button"
					onClick={() => onSel(it.key)}
					className={cn(
						"rounded-md px-2.5 py-1.5 text-left text-xs transition-colors duration-150",
						sel === it.key
							? "bg-brand-accent/10 font-medium text-brand-accent"
							: "text-text-primary hover:bg-surface-subtle",
					)}
					data-testid="plan-config-item"
				>
					<span className="text-text-muted">{pageOf(it.key)} · </span>
					{it.label}
				</button>
			))}
		</div>
	);
}

function ValuePane({
	item,
	row,
	seed,
}: { item: Item | null; row: PlanSnapshotRow; seed: boolean }) {
	if (!item) return <div className="text-sm text-text-muted">选一项看前后值</div>;
	const prev = row.prev_snapshot_id ?? "";
	return (
		<div className="space-y-2" data-testid="plan-config-value">
			<div className="text-sm font-semibold text-text-primary">
				{pageOf(item.key)} · {item.label}
			</div>
			{seed ? (
				item.long || typeof item.after === "object" ? (
					<CodeBlock code={show(item.after)} maxHeight={520} />
				) : (
					<div className="font-mono text-xs text-text-primary">{show(item.after)}</div>
				)
			) : item.long || typeof item.after === "object" || typeof item.before === "object" ? (
				<TextDiff
					before={show(item.before)}
					after={show(item.after)}
					beforeLabel={prev}
					afterLabel={row.snapshot_id}
				/>
			) : (
				<div className="flex flex-wrap items-center gap-2 text-xs" data-testid="plan-config-short">
					<span className="text-text-muted">前：</span>
					<code className="font-mono text-text-primary">{show(item.before)}</code>
					<span className="text-text-muted">（{prev}）</span>
					<span className="text-text-muted">→ 后：</span>
					<code className="font-mono text-text-primary">{show(item.after)}</code>
					<span className="text-text-muted">（{row.snapshot_id}）</span>
				</div>
			)}
		</div>
	);
}

/** 对比抽屉主体：本快照 vs 上一个（A-35）；种子快照没有上一版 → 完整配置（A-34） */
function SnapshotCompare({ row }: { row: PlanSnapshotRow }) {
	const seed = !row.prev_snapshot_id;
	const diff = usePlanConfigDiff(row.prev_snapshot_id, row.snapshot_id);
	const full = usePlanSnapshot(seed ? row.snapshot_id : null);
	const [sel, setSel] = useState<string | null>(null);
	const items: Item[] = useMemo(() => {
		if (!seed) {
			return (diff.data?.changes ?? []).map((c) => ({
				key: c.key,
				label: c.label,
				before: c.before,
				after: c.after,
				long: c.is_long_text,
			}));
		}
		const labels = new Map(row.changed_keys.map((k, i) => [k, row.changed_labels[i] ?? k]));
		return Object.entries(full.data?.content ?? {})
			.map(([key, v]) => ({
				key,
				label: labels.get(key) ?? key,
				after: v,
				long: typeof v === "string" && v.length > 120,
			}))
			.sort((a, b) => pageRank(a.key) - pageRank(b.key) || a.key.localeCompare(b.key));
	}, [seed, diff.data, full.data, row]);
	const q = seed ? full : diff;
	if (q.isLoading) return <LoadingInline text="加载对比…" />;
	if (q.isError)
		return (
			<FailState
				title="对比没加载出来"
				reason={planErrorMessage(q.error)}
				onRetry={() => q.refetch()}
				retrying={q.isFetching}
			/>
		);
	const cur = items.find((i) => i.key === sel) ?? items[0] ?? null;
	return (
		<div className="flex h-full min-h-0 flex-col">
			{seed ? (
				<div
					className="flex items-center gap-2 border-b border-surface-border bg-surface-subtle px-4 py-2 text-xs text-text-secondary"
					data-testid="plan-config-seed-note"
				>
					<Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
					首个快照，没有可对比的上一版。下面是这份快照的完整配置（只读）。
				</div>
			) : null}
			{!items.length ? (
				<div className="p-4 text-sm text-text-muted">两份配置内容相同。</div>
			) : (
				<div className="flex min-h-0 flex-1">
					<div className="w-64 shrink-0 overflow-y-auto border-r border-surface-border p-2">
						<div className="px-2.5 pb-1.5 text-xs font-medium text-text-secondary">
							{seed ? "配置项" : "改动项"}（{items.length}）
						</div>
						<ItemList items={items} sel={cur?.key ?? null} onSel={setSel} />
					</div>
					<div className="min-w-0 flex-1 overflow-y-auto p-4">
						<ValuePane item={cur} row={row} seed={seed} />
					</div>
				</div>
			)}
		</div>
	);
}

function envText(v: unknown): string {
	if (v === undefined || v === null) return "（空）";
	if (typeof v === "string" || typeof v === "number") return String(v);
	if (typeof v === "object" && v && "default" in v) {
		const b = v as { default?: string; skills?: Record<string, string> };
		const n = Object.keys(b.skills ?? {}).length;
		return `${b.default ?? "—"}${n ? `（${n} 个工具单独覆盖）` : ""}`;
	}
	return JSON.stringify(v);
}

/** 模型提供方（11-02）：提供方与来源、各节点平台模型名 → 实际发送名、价格口径；key 只说配没配 */
function LlmProviderText({ llm }: { llm?: PlanLlmProvider | null }) {
	if (!llm) return null;
	const models = llm.models.map((m) =>
		m.sent_as === m.model ? `${m.node} ${m.model}` : `${m.node} ${m.model} → ${m.sent_as}`,
	);
	const price = llm.price
		? Object.entries(llm.price.per_million)
				.map(
					([m, p]) =>
						`${m} 每百万 token 命中／未命中／输出 闲时 ${p.off.join("／")}、峰时 ${p.peak.join("／")} ${llm.price?.currency}`,
				)
				.join("；")
		: null;
	return (
		<span data-testid="plan-run-env-llm">
			模型提供方 <span className="font-mono">{llm.provider}</span>（
			{llm.source === "snapshot" ? "配置快照 plan.llm.provider" : "快照没写，按 .env 默认"}；
			<span className="font-mono">{llm.base_url}</span>；key {llm.key_env}
			{llm.key_configured ? " 已配" : " 没配"}）；实际发送模型{" "}
			<span className="font-mono">{models.join("，") || "—"}</span>
			{price ? (
				<>
					；实花按官网价格表算（{price}；峰时 UTC {llm.price?.peak_utc} 周一至周五），账本与花费闸按{" "}
					{llm.price?.cny_per_usd} 元 ＝ 1 美元折算，余额同口径
				</>
			) : (
				"；实花取提供方返回的 usage.cost（美元）"
			)}
		</span>
	);
}

/** 运行环境（收尾 B1）：影响运行的环境项都在快照里，同一快照能复现 */
function RunEnvCard() {
	const q = usePlanRunEnv();
	if (q.isLoading || !q.data) {
		return q.isError ? (
			<Card data-testid="plan-block-run-env">
				<CardContent className="p-0">
					<FailState
						title="运行环境没加载出来"
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
					/>
				</CardContent>
			</Card>
		) : null;
	}
	const d = q.data;
	return (
		<Card data-testid="plan-block-run-env">
			<CardContent className="space-y-3 p-4">
				<div className="flex flex-wrap items-center gap-2 text-sm">
					<span className="font-semibold text-text-primary">运行环境</span>
					<span className="text-xs text-text-muted">
						影响运行的环境项都进配置快照，一轮按本轮快照取，同一快照能复现；生效快照
					</span>
					<SnapshotTag id={d.snapshot_id} />
				</div>
				<PlanTable
					rows={d.items ?? []}
					rowKey={(r) => r.key}
					rowTestId="plan-run-env-row"
					density="compact"
					columns={[
						{
							key: "label",
							title: "环境项",
							render: (r) => (
								<span className="block max-w-[420px] truncate" title={`${r.label}（${r.key}）`}>
									{r.label.split("（")[0]}
									<span className="ml-1.5 font-mono text-xs text-text-muted">{r.key}</span>
								</span>
							),
						},
						{
							key: "value",
							title: "快照里的值",
							render: (r) =>
								r.in_snapshot ? (
									<code className="font-mono text-xs">{envText(r.value)}</code>
								) : (
									<Badge tone="warning">快照里没有</Badge>
								),
						},
						{
							key: "effective",
							title: "运行时取到",
							render: (r) => (
								<span className="inline-flex items-center gap-1.5">
									<code className="font-mono text-xs">{envText(r.effective)}</code>
									{r.in_snapshot ? null : <span className="text-xs text-text-muted">按旧口径</span>}
								</span>
							),
						},
					]}
				/>
				<div
					className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-secondary"
					data-testid="plan-run-env-pack"
				>
					<span>
						业务包 <span className="font-mono">{d.pack?.name ?? "—"}</span>
						（业务参数与平台口径指纹{" "}
						<span className="font-mono">{d.pack?.params_digest ?? "—"}</span>
						、pack.json 指纹 <span className="font-mono">{d.pack?.pack_digest ?? "—"}</span>
						；随代码走 PR，不进快照，记进每批 ／ 每轮运行资产）
					</span>
					<span>
						Jev 最近实际返回 <span className="font-mono">{d.jev_seen ?? "—"}</span>
					</span>
					<span>
						代码版本 <span className="font-mono">{d.code_version ?? "—"}</span>
					</span>
					<LlmProviderText llm={d.llm} />
					<span data-testid="plan-run-env-dict">
						标签字典版本 <span className="font-mono">{d.tag_dict_version ?? "—"}</span>
						（生效快照保存时记的{" "}
						<span className="font-mono">
							{d.snapshot_tag_dict_version ?? "没记，批 5 之前保存"}
						</span>
						；字典随代码与业务包走，见 <PageJump to="tags" />）
					</span>
				</div>
			</CardContent>
		</Card>
	);
}

export function ConfigVersionsTab() {
	const snaps = usePlanSnapshots();
	const search = useSearch({ from: "/plan-lab" });
	const navigate = useNavigate();
	const [page, setPage] = useState(0);
	const [seq, setSeq] = useState<number | null>(null);
	const openId = search.snapshot ?? null;

	// 从外部快照号进来时停在该快照所在的那一页
	const rows = snaps.data?.rows ?? [];
	useEffect(() => {
		if (!openId || seq != null) return;
		const idx = rows.findIndex((r) => r.snapshot_id === openId);
		if (idx >= 0) setPage(Math.floor(idx / PAGE));
	}, [openId, rows, seq]);

	const openRow = openId
		? (rows.find((r) => r.snapshot_id === openId && (seq == null || r.seq === seq)) ??
			rows.find((r) => r.snapshot_id === openId))
		: undefined;
	const notFound = !!openId && !!snaps.data && !openRow;

	function open(r: PlanSnapshotRow) {
		setSeq(r.seq);
		navigate({
			to: "/plan-lab",
			search: { snapshot: r.snapshot_id },
			hash: "config-versions",
			replace: true,
		});
	}
	function close() {
		setSeq(null);
		navigate({ to: "/plan-lab", search: {}, hash: "config-versions", replace: true });
	}

	if (snaps.isLoading) {
		return (
			<Card data-testid="plan-block-snapshots">
				<CardContent className="p-6">
					<LoadingInline text="加载快照列表…" />
				</CardContent>
			</Card>
		);
	}
	if (snaps.isError || !snaps.data) {
		return (
			<Card data-testid="plan-block-snapshots">
				<CardContent className="p-0">
					<FailState
						title="没取到快照列表"
						reason={snaps.error ? planErrorMessage(snaps.error) : "接口没有返回"}
						onRetry={() => snaps.refetch()}
						retrying={snaps.isFetching}
						testId="plan-config-fail"
					/>
				</CardContent>
			</Card>
		);
	}

	const pageCount = Math.max(1, Math.ceil(rows.length / PAGE));
	const p = Math.min(page, pageCount - 1);
	const pageRows = rows.slice(p * PAGE, p * PAGE + PAGE);

	return (
		<>
			{notFound ? (
				<Card data-testid="plan-block-notfound">
					<CardContent className="flex items-center gap-2 px-4 py-3 text-sm text-text-secondary">
						<Info className="h-4 w-4 shrink-0 text-brand-warning" aria-hidden />
						找不到快照 <code className="font-mono">{openId}</code>（可能是链接写错了）
					</CardContent>
				</Card>
			) : null}
			<RunEnvCard />
			<Card data-testid="plan-block-snapshots">
				<CardContent className="space-y-3 p-4">
					{rows.length ? (
						<PlanTable
							rows={pageRows}
							rowKey={(r) => `${r.seq ?? ""}:${r.snapshot_id}`}
							rowTestId="plan-snapshot-row"
							onRowClick={open}
							columns={[
								{
									key: "id",
									title: "快照号",
									render: (r) => (
										<span className="inline-flex items-center gap-1.5">
											<SnapshotTag id={r.snapshot_id} onOpen={() => open(r)} />
											{r.is_active ? <Badge tone="success">当前生效</Badge> : null}
											{isSeed(r) ? <Badge tone="neutral">种子</Badge> : null}
										</span>
									),
								},
								{ key: "at", title: "保存时间", render: (r) => fmtAt(r.at) },
								{ key: "op", title: "谁", render: (r) => opName(r.operator) },
								{
									key: "dict",
									title: "字典版本",
									render: (r) =>
										r.tag_dict_version ? (
											<span className="font-mono text-xs">{r.tag_dict_version}</span>
										) : (
											<span className="text-text-muted" title="批 5 之前保存的快照没记字典版本">
												—
											</span>
										),
								},
								{
									key: "changed",
									title: "改了哪几项",
									render: (r) =>
										r.prev_snapshot_id ? (
											<CellTags items={r.changed_labels} max={2} />
										) : (
											<span className="text-text-muted">（首版）</span>
										),
								},
								{
									key: "note",
									title: "备注",
									className: "max-w-[280px] truncate",
									render: (r) =>
										r.note ? (
											<span title={r.note}>{r.note}</span>
										) : (
											<span className="text-text-muted">—</span>
										),
								},
							]}
						/>
					) : (
						<EmptyState
							title="还没有配置快照"
							desc="每保存一次配置生成一个快照，在这里看谁、何时、改了什么。"
						/>
					)}
					<div className="flex items-center justify-between gap-3 text-xs text-text-muted">
						<span data-testid="plan-snapshot-total">共 {rows.length} 条</span>
						<Pagination page={p} pageCount={pageCount} onChange={setPage} />
					</div>
				</CardContent>
			</Card>
			<Drawer
				open={!!openRow}
				onClose={close}
				width="min(1180px,95vw)"
				testId="plan-config-drawer"
				flush
				title={
					openRow ? (
						<span className="font-mono">
							{openRow.snapshot_id}
							{openRow.prev_snapshot_id ? ` ← ${openRow.prev_snapshot_id}` : ""}
						</span>
					) : (
						""
					)
				}
				badges={
					openRow ? (
						<>
							{openRow.is_active ? <Badge tone="success">当前生效</Badge> : null}
							{isSeed(openRow) ? <Badge tone="neutral">种子</Badge> : null}
						</>
					) : null
				}
				description={
					openRow
						? `${opName(openRow.operator)} · ${fmtAt(openRow.at)} · 字典版本 ${openRow.tag_dict_version ?? "—"} · 备注：${openRow.note || "—"}`
						: undefined
				}
			>
				{openRow ? (
					<SnapshotCompare key={`${openRow.seq}:${openRow.snapshot_id}`} row={openRow} />
				) : null}
			</Drawer>
		</>
	);
}
